import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Config } from "../config";
import { TextField, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

function AIChat({ className, control, onComplianceLevelDetected, onAddComment }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [loadingDots, setLoadingDots] = useState('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  /*
  useEffect(() => {
    scrollToBottom();
  }, [messages]);*/

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingDots(dots => dots.length >= 3 ? '' : dots + '.');
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  const extractComplianceLevel = (text) => {
    if (!text) {
      console.error('No text provided to extractComplianceLevel');
      return null;
    }
    
    const content = String(text).toLowerCase();
    
    // Use regex to match compliance levels with percentages
    if (/no\s*cumple\s*\(0%\)/i.test(content)) {
      return 1;
    }
    if (/cumple\s*parcialmente\s*\(50%\)/i.test(content)) {
      return 2;
    }
    if (/cumple\s*totalmente\s*\(100%\)/i.test(content)) {
      return 3;
    }
    
    // Fallback: match by keywords without percentages
    if (/no\s*cumple/i.test(content) && !/cumple\s*(parcialmente|totalmente)/i.test(content)) {
      return 1;
    }
    if (/cumple\s*parcialmente/i.test(content)) {
      return 2;
    }
    if (/cumple\s*totalmente/i.test(content)) {
      return 3;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const newMessage = {
      role: 'user',
      content: inputMessage
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(Config.APIBaseURL + '/analyze', {
        messages: [...messages, newMessage],
        control: control
      }, {
        headers: {
          'Content-Type': 'application/json',
          'access_token': `Bearer ${token}`
        },
        withCredentials: true
      });

      let responseContent = null;
      
      // Handle the nested response format from the API
      if (response.data && response.data.choices && 
          response.data.choices[0] && 
          response.data.choices[0].message && 
          response.data.choices[0].message.content) {

        responseContent = response.data.choices[0].message.content;
        
        const assistantMessage = {
          role: 'assistant',
          content: responseContent
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else if (response.data && response.data.message) {
        // Fallback to the original expected format
        responseContent = response.data.message;
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Received response in unexpected format.'
        }]);
        return;
      }

      const complianceLevel = extractComplianceLevel(responseContent);
      if (complianceLevel !== null && onComplianceLevelDetected) {
        onComplianceLevelDetected(control.ref, complianceLevel);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request.'
      }]);
    } finally {
      setIsLoading(false);
      setLoadingDots('');
    }
  };

  const handleAddComment = (message) => {
    if (onAddComment && control) {
      onAddComment(control.ref, message.content);
    }
  };

  return (
    <div className={`flex flex-col h-full max-w-xl mx-auto ${className}`}>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className="flex items-start gap-2">
              <div
                className={`max-w-[75%] rounded-lg p-3 ${
                  message.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-700 text-slate-200'
                }`}
              >
                {message.content}
              </div>
              {message.role === 'user' && (
                <button
                  className="btn p-1 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-slate-300" 
                  title="Agregar como comentario"
                  onClick={() => handleAddComment(message)}
                >
                <svg className="w-3 h-3 fill-current text-white" viewBox="0 0 24 24">
                  <path d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z" />
                </svg>
                </button>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-700 text-slate-200 rounded-lg p-3">
              Analizando{loadingDots}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSubmit} className="p-4">
        <div className="relative flex justify-center">
          <TextField
            fullWidth
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Describe el estado de cumplimiento con el control"
            disabled={isLoading}
            sx={{
              width: '90%',
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(30, 41, 59, 0.8)',
                borderRadius: '12px',
                '&:hover': {
                  bgcolor: 'rgba(30, 41, 59, 0.9)',
                },
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  transition: 'border-color 0.2s',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4f46e5 !important',
                  borderRadius: '12px',
                  borderWidth: '1px !important',
                  boxShadow: 'none',
                },
                '&.Mui-focused': {
                  outline: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '12px',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f46e5 !important',
                  borderWidth: '1px !important',
                  boxShadow: 'none !important',
                },
                '&:focus, &:focus-visible, &:focus-within': {
                  outline: 'none !important',
                  boxShadow: 'none !important',
                },
                '& input': {
                  paddingRight: '40px',
                  fontSize: '0.75rem',
                  '&:focus, &:focus-visible': {
                    outline: 'none !important',
                    boxShadow: 'none !important',
                  },
                }
              },
              '& .MuiInputBase-input': {
                color: '#e2e8f0',
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(226, 232, 240, 0.5)',
                opacity: 1,
              }
            }}
          />
          <IconButton 
            type="submit"
            disabled={isLoading || !inputMessage.trim()}
            sx={{
              position: 'absolute',
              right: 'calc(5% + 8px)',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <SendIcon sx={{ 
              color: inputMessage.trim() ? '#e2e8f0' : 'rgba(226, 232, 240, 0.3)',
              '&.Mui-disabled': {
                color: 'rgba(226, 232, 240, 0.3)'
              }
            }} />
          </IconButton>
        </div>
      </form>
    </div>
  );
}

export default AIChat; 